import {
  getCurrentMockUser,
  seedCurrentUserMockScenario,
} from './mockOrganizationStore';

export const DEMO_SCENARIOS = [
  { id: 'new-activated-user', title: 'New Activated User', description: 'Active account with no organization membership.' },
  { id: 'single-organization', title: 'Single Organization User', description: 'One administrator membership; login opens Dashboard.' },
  { id: 'multi-organization', title: 'Multi-Organization User', description: 'Three memberships; login opens Tenant Selection.' },
  { id: 'different-roles', title: 'Different Roles', description: 'Organization Admin in A and Reviewer in B.' },
  { id: 'pending-invitation', title: 'Pending Invitation', description: 'Admin in A with a pending Reviewer invitation to B.' },
  { id: 'separate-billing', title: 'Separate Billing', description: 'Paid, not-started, and pending-payment organizations.' },
];

const slug = (value) => String(value || 'user').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const organization = (userKey, key, overrides = {}) => ({
  id: `demo-${userKey}-organization-${key}`,
  name: `Demo Organization ${key.toUpperCase()}`,
  identifier: `demo-${userKey}-${key}`,
  email: `admin-${key}@demo.worksplaniq.local`,
  setupStatus: 'COMPLETE',
  workspaceSetupStatus: 'COMPLETE',
  plan: 'Professional',
  subscriptionStatus: 'ACTIVE',
  paymentStatus: 'PAID',
  accessStatus: 'FULL_ACCESS',
  status: 'active',
  orgId: `demo-backend-org-${userKey}-${key}`,
  wcId: `demo-calendar-${userKey}-${key}`,
  source: 'demo-scenario',
  ...overrides,
});

const membership = (userKey, organizationId, role, roleId) => ({
  id: `demo-${userKey}-membership-${organizationId}`,
  organizationId,
  role,
  roleId,
  status: 'ACTIVE',
  permissions: [],
});

const ADMIN_ROLE_ID = '0f418ead-0b68-4af4-b141-0b894503f559';
const REVIEWER_ROLE_ID = '5febd304-7990-4ddd-a9ce-18812aae2a4f';

export const applyDemoScenario = (scenarioId) => {
  const user = getCurrentMockUser();
  const userKey = slug(user.id || user.email);
  const organizationA = organization(userKey, 'a');
  const organizationB = organization(userKey, 'b', { plan: 'Enterprise' });
  const organizationC = organization(userKey, 'c', {
    plan: 'Plan not selected', subscriptionStatus: 'NO_SUBSCRIPTION', paymentStatus: 'NOT_STARTED', accessStatus: 'EXPLORATION',
  });
  let organizations = [];
  let memberships = [];
  let invitations = [];
  let entryPath = null;

  if (scenarioId === 'single-organization') {
    organizations = [organizationA];
    memberships = [membership(userKey, organizationA.id, 'Organization Administrator', ADMIN_ROLE_ID)];
  } else if (scenarioId === 'multi-organization') {
    organizations = [organizationA, organizationB, organizationC];
    memberships = [
      membership(userKey, organizationA.id, 'Organization Administrator', ADMIN_ROLE_ID),
      membership(userKey, organizationB.id, 'Reviewer', REVIEWER_ROLE_ID),
      membership(userKey, organizationC.id, 'Organization Administrator', ADMIN_ROLE_ID),
    ];
  } else if (scenarioId === 'different-roles') {
    organizations = [organizationA, organizationB];
    memberships = [
      membership(userKey, organizationA.id, 'Organization Administrator', ADMIN_ROLE_ID),
      membership(userKey, organizationB.id, 'Reviewer', REVIEWER_ROLE_ID),
    ];
  } else if (scenarioId === 'pending-invitation') {
    const invitationId = `demo-${userKey}-pending-invitation`;
    organizations = [organizationA, organizationB];
    memberships = [membership(userKey, organizationA.id, 'Organization Administrator', ADMIN_ROLE_ID)];
    invitations = [{
      invitationId,
      organizationId: organizationB.id,
      organizationName: organizationB.name,
      inviteeEmail: user.email,
      role: 'Reviewer',
      roleId: REVIEWER_ROLE_ID,
      status: 'PENDING',
      invitedBy: 'Demo Organization B Administrator',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString(),
      source: 'demo-scenario',
    }];
    entryPath = `/invitation/${invitationId}`;
  } else if (scenarioId === 'separate-billing') {
    const noBillingOrganization = organization(userKey, 'b', {
      plan: 'Plan not selected', subscriptionStatus: 'NO_SUBSCRIPTION', paymentStatus: 'NOT_STARTED', accessStatus: 'EXPLORATION',
    });
    const pendingOrganization = organization(userKey, 'c', {
      plan: 'Professional', subscriptionStatus: 'INVOICE_PENDING', paymentStatus: 'PENDING_PAYMENT', accessStatus: 'TEMPORARY_ACCESS',
      invoiceNumber: `INV-DEMO-${userKey}-C`, invoiceIssuedAt: new Date().toISOString(),
      paymentDueAt: new Date(Date.now() + (14 * 24 * 60 * 60 * 1000)).toISOString(),
    });
    organizations = [organizationA, noBillingOrganization, pendingOrganization];
    memberships = organizations.map((item) => membership(userKey, item.id, 'Organization Administrator', ADMIN_ROLE_ID));
  } else if (scenarioId !== 'new-activated-user') {
    throw new Error('Unknown demo scenario.');
  }

  seedCurrentUserMockScenario({ organizations, memberships, invitations });
  sessionStorage.setItem('workplansiq.activeDemoScenario', scenarioId);
  return { scenarioId, organizations, memberships, invitations, entryPath };
};
